import {useEffect, useRef, type ReactNode} from 'react';
import {useHermes} from './lib/store';
import type {HermesState} from './lib/types';
import {APP_BASE, Link, usePath, useSearch} from './router';
import {Brand, Empty, ErrorNotice, Loading, PageHeading} from './components';
import {AuthPage, type AuthMode} from './auth';
import {AccountFilesContent, AccountSettingsContent} from './settings';
import {NativeWorkspace} from './hermes';
import {ModelSelector} from './model-selector';
import {WorkspaceUsage} from './workspace-usage';
import {PRODUCT_ENTRY_LABEL, PRODUCT_NAME} from '../src/product';
import {MarketingHome, YourTeamPage, HowItWorksPage} from './marketing';
import {workspaceContextQuery} from './lib/workspace-location';
import {DeveloperPage, CompanyPage, PricingPage, NewsPage, ContactSalesPage, DownloadPage} from './marketing-pages';
import {SOLUTION_PAGES, SolutionsPage} from './solutions';
import {AgentPage, AgentWorkPage, agentRoleFromPath, agentWorkFromPath} from './agent-pages';
import {getTeamRole} from '../src/team';
import {resolvePublicPath, TEAM_PAGE_PATH} from './public-paths';
import {LEGAL_PAGES} from './legal-pages';
import './shell.css';
import {MainLoadingBar, MainLoadingProvider} from './main-loading';

type Actions = ReturnType<typeof useHermes>['actions'];
type PageProps = {state: HermesState; actions: Actions};
const navigation = [{path: '/workspace', label: 'Workspace'}, {path: '/agents', label: 'Agents'}, {path: '/tasks', label: 'Tasks'},
  {path: '/files', label: 'Files'}, {path: '/settings', label: 'Settings'}] as const;
const accountNavigation = navigation.filter(link => ['/workspace', '/files', '/settings'].includes(link.path));
const publicPages = [{path: '/', label: 'Product', component: MarketingHome},
  ...LEGAL_PAGES,
  ...SOLUTION_PAGES,
  {path: '/how-it-works', label: 'How it works', component: HowItWorksPage},
  {path: TEAM_PAGE_PATH, label: 'Your team', component: YourTeamPage},
  {path: '/solutions', label: 'Solutions', component: SolutionsPage},
  {path: '/developer', label: 'Developer', component: DeveloperPage},
  {path: '/company', label: 'Company', component: CompanyPage},
  {path: '/pricing', label: 'Pricing', component: PricingPage},
  {path: '/news', label: 'News', component: NewsPage},
  {path: '/contact-sales', label: 'Contact Sales', component: ContactSalesPage},
  {path: '/download', label: 'Download', component: DownloadPage}] as const;
const marketingNavigation = publicPages.filter(page => [TEAM_PAGE_PATH,'/solutions','/developer','/company','/pricing','/news'].includes(page.path));
const marketingActions = publicPages.filter(page => ['/contact-sales','/download'].includes(page.path));

function Header({state, actions, path, search}: PageProps & {path: string; search: string}) {
  const principal = state.session.data;
  const workQuery = workspaceContextQuery(search);
  if (principal && accountNavigation.some(link => link.path === path)) {
    const requestedChat = new URLSearchParams(search).get('chat');
    const detail = state.nativeDetail.data;
    const currentChat = state.nativeConnection.status === 'open' && detail
      && detail.session.sessionId === state.nativeTarget?.sessionId
      && (requestedChat === null || requestedChat === detail.session.sessionId) ? detail : null;
    const disabledReason = state.session.status !== 'ready' ? 'Checking account…'
      : state.accountSettings.status === 'error' || state.nativeRuntime.status === 'error' ? 'Models unavailable'
      : state.accountSettings.status !== 'ready' || state.nativeRuntime.status !== 'ready' ? 'Checking model access…'
      : !state.accountSettings.data?.modelKey.configured ? 'Add API key in Settings'
      : !state.nativeRuntime.data?.running ? 'Start team in Settings'
      : requestedChat !== null && currentChat === null ? 'Chat not connected' : null;
    const observationKey = JSON.stringify(currentChat ? [currentChat.state.running, currentChat.state.resuming,
      currentChat.state.model, currentChat.state.pendingDelegations,
      currentChat.agents.map(agent => [agent.sessionId, agent.running, agent.resuming, agent.pendingDelegations])] : null);
    return <header className="site-header account-header"><div className="header-inner">
      <ModelSelector accountId={principal.accountId} sessionId={currentChat?.session.sessionId ?? null}
        disabledReason={disabledReason} observationKey={observationKey}/>
      <WorkspaceUsage state={state} sessionId={currentChat?.session.sessionId ?? null}/>
      <div className="account-header-controls">
        <div className="account-header-profile">
          {state.accountProfile.data ? <Link className="account-header-name" href={`${APP_BASE}/settings${workQuery}`}
            title={state.accountProfile.data.username}>{state.accountProfile.data.username}</Link>
            : state.accountProfile.status !== 'error' && <Loading active={state.accountProfile.status === 'idle' || state.accountProfile.status === 'loading'}/>}
        </div>
        <nav className="account-navigation" aria-label="Account navigation">
          {accountNavigation.map(link =>
            <Link key={link.path} href={`${APP_BASE}${link.path}${workQuery}`}
              aria-current={path === link.path ? 'page' : undefined}>{link.label}</Link>)}
          <button type="button" className="text-button" disabled={state.session.status !== 'ready' || state.operation.label !== null}
            onClick={() => void actions.signOut().catch(() => undefined)}>Sign out</button>
        </nav>
      </div>
      <div className="account-header-errors">
        <ErrorNotice error={state.accountProfile.error}/>
        <ErrorNotice error={state.accountSettings.error}/>
        <ErrorNotice error={state.nativeRuntime.error}/>
      </div>
    </div></header>;
  }
  return <header className="site-header"><div className="header-inner"><Brand/>
    <nav className="primary-navigation" aria-label="Main navigation">
      {marketingNavigation.map(link => {
        const active = path === link.path || (link.path === TEAM_PAGE_PATH && path.startsWith(`${TEAM_PAGE_PATH}/`))
          || (link.path === '/solutions' && SOLUTION_PAGES.some(page => page.path === path));
        return <Link key={link.path} href={`${APP_BASE}${link.path}`} className={active ? 'active' : undefined}
          aria-current={path === link.path ? 'page' : undefined}>{link.label}</Link>;
      })}
      {marketingActions.map(link => <Link key={link.path} href={`${APP_BASE}${link.path}`}
        className={`mobile-navigation-action${path === link.path ? ' active' : ''}`} aria-current={path === link.path ? 'page' : undefined}>{link.label}</Link>)}
    </nav><div className="header-action">{marketingActions.map(link => <Link key={link.path} className="button button-quiet" href={`${APP_BASE}${link.path}`}>{link.label}</Link>)}
      <Link className="button" href={principal ? `${APP_BASE}/workspace${workQuery}` : `${APP_BASE}/login`}>
        {principal ? PRODUCT_ENTRY_LABEL : 'Sign in'}</Link></div>
  </div></header>;
}

function Footer() {
  return <footer className="site-footer"><span className="footer-name">EightBots</span>
    <nav aria-label="Footer navigation"><Link href={`${APP_BASE}/how-it-works`}>How it works</Link>
      <Link href={`${APP_BASE}/pricing`}>Pricing</Link><Link href={`${APP_BASE}/contact-sales`}>Contact sales</Link>
      {LEGAL_PAGES.map(page => <Link key={page.path} href={`${APP_BASE}${page.path}`}>{page.label}</Link>)}</nav>
  </footer>;
}

function AccountGate({state, actions, children}: PageProps & {children: ReactNode}) {
  if (state.session.status === 'idle' || state.session.status === 'loading') return <Loading/>;
  if (state.session.status === 'error') return <ErrorNotice error={state.session.error}/>;
  if (!state.session.data) return <AuthPage state={state} actions={actions} mode="login" returnTo={`${window.location.pathname}${window.location.search}`}/>;
  return <>{children}</>;
}

function ServiceStatus({state}: Pick<PageProps, 'state'>) {
  return <section className="service-status"><div className="section-heading"><h2>Availability</h2></div>
    <ErrorNotice error={state.health.error}/>
    {!state.health.data && state.health.status !== 'error' && <Loading/>}
    {state.health.data && (state.health.data.status === 'healthy' ? <p className="field-help">The service is available.</p>
      : <div className="error-notice" role="alert"><p>The service is not available.</p>
        <details open><summary>Full error details</summary><pre>{JSON.stringify(state.health.data, null, 2)}</pre></details></div>)}
  </section>;
}

export default function App() {
  const {state, actions} = useHermes();
  const mainRef = useRef<HTMLElement>(null);
  const current = usePath();
  const search = useSearch();
  const requestedPath = current === APP_BASE || current === `${APP_BASE}/` ? '/' : current.slice(APP_BASE.length);
  const path = requestedPath === '/demo' ? '/workspace' : resolvePublicPath(requestedPath);
  const publicPage = publicPages.find(item => item.path === path);
  const PublicPage = publicPage?.component;
  const agentRole = agentRoleFromPath(path);
  const workRole = agentWorkFromPath(path);
  const authMode: AuthMode | null = path === '/login' ? 'login' : path === '/register' ? 'register' : path === '/setup-login' ? 'setup-login' : null;
  const workQuery = workspaceContextQuery(search);
  useEffect(() => {void actions.load();}, [actions]);
  useEffect(() => {
    if (requestedPath === path) return;
    const query = requestedPath === '/demo' ? workQuery : search;
    window.history.replaceState(window.history.state, '', `${APP_BASE}${path}${query}${window.location.hash}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [requestedPath, path, search, workQuery]);
  useEffect(() => {
    const name = authMode === 'login' ? 'Sign in' : authMode === 'register' ? 'Create account' : authMode === 'setup-login' ? 'Set up sign-in'
      : workRole ? `${getTeamRole(workRole).name} work` : agentRole ? getTeamRole(agentRole).name : publicPage?.label ?? navigation.find(item => item.path === path)?.label;
    document.title = `${name ?? 'Page not found'} · ${PRODUCT_NAME}`;
    mainRef.current?.scrollTo({top: 0, behavior: 'instant'});
  }, [path]);
  const marketing = Boolean(PublicPage) || agentRole !== null || workRole !== null;
  const authSurface = authMode !== null || (!marketing && !state.session.data);
  const workspacePage = path === '/tasks' ? 'tasks' : path === '/files' ? 'files' : path === '/settings' ? 'settings' : 'workspace';
  return <MainLoadingProvider><div className={`app-shell ${marketing ? 'public-site' : authSurface ? 'auth-site' : 'workspace-site'}`}>
    <Header state={state} actions={actions} path={path} search={search}/>
    <main ref={mainRef} className={`app-main ${marketing ? 'marketing-page' : authSurface ? 'auth-main' : 'workspace-app-page'}`}>
      <MainLoadingBar/>
      <Loading active={state.operation.label !== null || (!marketing && !authSurface && state.nativeConnection.status === 'connecting')}/>
      {!marketing && !authSurface && state.operation.error && <aside className="operation-notice" aria-live="polite">
      <ErrorNotice error={state.operation.error}/>
      {state.operation.error && <button className="text-button" type="button" onClick={actions.clearError}>Dismiss error</button>}
    </aside>}
      {!marketing && <ErrorNotice error={state.config.error}/>}
      {PublicPage ? <PublicPage/> : workRole ? <AgentWorkPage roleId={workRole}/> : agentRole ? <AgentPage roleId={agentRole}/> : authMode ? <AuthPage key={authMode} state={state} actions={actions} mode={authMode}/> : navigation.some(item => item.path === path) ? <>
        <AccountGate state={state} actions={actions}><NativeWorkspace state={state} actions={actions} view={workspacePage}>
          {path === '/files' ? <><PageHeading title="Instructions and files">Read saved files and change your team’s instructions.</PageHeading><AccountFilesContent state={state} actions={actions}/></>
            : path === '/settings' ? <><PageHeading title="Settings" action={<Link className="button" href={`${APP_BASE}/workspace${workQuery}`}>{PRODUCT_ENTRY_LABEL}</Link>}>Manage your account and connected services.</PageHeading><AccountSettingsContent state={state} actions={actions}/><ServiceStatus state={state}/></>
              : undefined}
        </NativeWorkspace></AccountGate></>
        : <div className="account-gate"><Brand/><Empty title="This page does not exist." action={<Link className="button" href={`${APP_BASE}/workspace${workQuery}`}>{PRODUCT_ENTRY_LABEL}</Link>}/></div>}
    </main>
    <Footer/>
  </div></MainLoadingProvider>;
}
